/* eslint-disable react/display-name */
import React, { memo } from  'react';
import styled from 'styled-components';

import { translate } from '../../common/translation/translate';
import { largeMediaQuery, mediumMediaQuery, smallMediaQuery } from '../../common/utilities/sizeHelpers';
import { IconWidget } from '@weatherbug/react';
import { AlertsListHeader } from '../alerts/alertsListHeader';
import { CircleSolidColorIcon } from '../common/icons/circleSolidColorIcon';
import { IStdParameters, OutdoorSportsForecastRecordType } from '../common/interfaces/types';
import { withScreenSize } from '../common/utilities/withScreenSize.jsx';
import { DisplayAdWidget } from '../ads/displayAdWidget.jsx';
import { HourlyOutdoorIndexLoadingUI } from './fallbackUI/hourlyOutdoorIndexLoadingUI';
import { OutdoorSportsLoadingErrorUI } from './fallbackUI/outdoorSportsLoadingErrorUI';
import { DisplayAdUnit } from '../../containers/ads/displayAdUnitContainer';

interface HourlyOurdoorIndexType {
    authId: string;
    secret: string;
    contentMgmtBaseUrl: string;
    stdParameters: IStdParameters;
    resourceList: string;
    forecastIconSet: string;
    forecastIconSize: string;
    outdoorSportsDataIsFetching: boolean;
    forecastRecords: OutdoorSportsForecastRecordType[];
    isApiError: boolean;
    isDesktop: boolean;
    isTablet: boolean;
    isMobile: boolean;
}

export const HourlyOutdoorIndex = withScreenSize(memo(({
    authId,
    secret,
    stdParameters,
    contentMgmtBaseUrl,
    resourceList,
    forecastIconSet,
    forecastIconSize,
    outdoorSportsDataIsFetching,
    forecastRecords,
    isApiError,
    isDesktop,
    isTablet,
    isMobile
}: HourlyOurdoorIndexType) => {
    return (
        <Container>
            <AlertsListHeader>{`${translate('hourlyOutdoorIndex')}`}</AlertsListHeader>
            { outdoorSportsDataIsFetching && <HourlyOutdoorIndexLoadingUI /> }
            { !outdoorSportsDataIsFetching && !forecastRecords && isApiError && <OutdoorSportsLoadingErrorUI /> }
            <>
                {forecastRecords?.map((forecastRecord, i) =>  {
                    const showBorderBottom = i < forecastRecords.length - 1;
                    return (
                        <RowContainer key={i}>
                            <HourlyOutdoorIndexRow showBorderBottom={showBorderBottom}>
                                <LeftTextWrapper>
                                    <TimeText>{forecastRecord?.dateTimeHourDisplayStr}</TimeText>
                                    <LabelText>{forecastRecord?.dateTimeDayOfWeekDisplayStr}</LabelText>
                                </LeftTextWrapper>
                                <IconWrapper>
                                    <CircleSolidColorIcon 
                                        backgroundFillColor={forecastRecord?.color} 
                                        displayValue={forecastRecord?.indexNumber.toString()} 
                                    />
                                </IconWrapper>
                                <IconWrapper>
                                    <IconWidget 
                                        iconCode={forecastRecord?.forecastIcon}
                                        iconSet={forecastIconSet}
                                        iconSize={forecastIconSize}
                                        authId={authId}
                                        secret={secret}
                                        contentManagementBaseUrl={contentMgmtBaseUrl}
                                        stdParameters={stdParameters}
                                        resourceList={resourceList}>
                                    </IconWidget>
                                </IconWrapper>
                                <CenterTextWrapper>
                                    <TempText>{`${forecastRecord?.temperature}°`}</TempText>
                                    <LabelText>{`${translate('temp')}`}</LabelText>
                                </CenterTextWrapper>
                                <RightTextWrapper>
                                    <TempText>{`${forecastRecord?.precipProbabilityPercent100}%`}</TempText>
                                    <LabelText>{`${translate('precipProb')}`}</LabelText>
                                </RightTextWrapper>
                            </HourlyOutdoorIndexRow>
                            {i === 6 && <AdRow showBorderBottom={true}>
                                {isDesktop && <AdContainer>
                                    <DisplayAdUnit
                                        slotId={'outdoorsports_desk_index'}
                                        height={90}
                                        width={728}
                                    />
                                </AdContainer>}
                                {isTablet && <AdContainer>
                                    <DisplayAdUnit
                                        slotId={'outdoorsports_tablet_index'}
                                        height={90}
                                        width={728}
                                    />
                                </AdContainer>}
                                {isMobile && <AdContainer>
                                    <DisplayAdUnit
                                        slotId={'outdoorsports_mobile_index_x83'}
                                        height={50}
                                        width={320}
                                    />
                                </AdContainer>}
                            </AdRow>}
                        </RowContainer>
                    );
                })}
            </>
        </Container>
    );
}));

const Container = styled.div`
    display: flex;
    flex-direction: column;
    background-color: #F9F9F9;
    height: 1465px;
    width: 100%;
    font-family: Source Sans Pro;
    font-style: normal;
    font-weight: 500;
    font-size: 15px;
    color: #555555;
    margin: 10px 10px 0 0;
    text-align: center;

    @media ${mediumMediaQuery} {
        margin-right: 0;
    }

    @media ${smallMediaQuery} {
        height: 1400px;
        margin: 10px 0;
    }
`;

const RowContainer = styled.span`
    width: 100%;
`;

const HourlyOutdoorIndexRow = styled.div<{ showBorderBottom: boolean; }>`
    display: flex;
    flex-direction: row;
    width: 100%;
    border-bottom: ${(props) => props.showBorderBottom ? '1px solid #EEEEEE': '' };
    padding: 10px 10px;
    justify-content: space-between;
`;

const LeftTextWrapper = styled.div`
    flex-direction: column;
    margin-top: 10px;
    width: 20%;

    @media ${smallMediaQuery} {
        text-align: left;
    }
`;

const CenterTextWrapper = styled.div`
    flex-direction: column;
    margin-top: 10px;
    width: 20%;
`;

const RightTextWrapper = styled.div`
    flex-direction: column;
    margin-top: 10px;
    width: 20%;

    @media ${smallMediaQuery} {
        text-align: right;
    }
`;

const TimeText = styled.div`
    font-weight: 600;
    font-size: 16px;
`;

const LabelText = styled.div`
    font-weight: 400;
    font-size: 13px;

    @media ${smallMediaQuery} {
        font-size: 12px;
    }
`;

const IconWrapper = styled.div`
    width: 40px;
    margin: 10px auto 0 auto;
    & .wb-content-mgt-icon-image {
            width: 40px;
            height: 40px;
    }

`;

const TempText = styled.div`
    font-weight: 600;
    font-size: 16px;
`;

const AdRow = styled.div<{ showBorderBottom: boolean; }>`
    display: flex;
    flex-direction: row;
    width: 100%;
    border-bottom: ${(props) => props.showBorderBottom ? '1px solid #EEEEEE': '' };
    justify-content: space-between;
`;

const AdContainer = styled.div`
    width: 100%;    
    justify-content: center;
    align-items: center;
    padding: 10px 20px;
     @media ${largeMediaQuery} {
        margin-top: 10px;
    }
`;
